import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { exportToExcel } from '@/utils/exportUtils';
import { formatINR, toInputDate } from '@/utils/format';
import { getData } from '@/services/FetchNodeServices.js';
import { useWarehouseStore } from '@/stores/warehouseStore';

interface ProductionSummaryRow {
  id: string;
  poNumber: string;
  product: string;
  productCode: string;
  type: string;
  plannedQty: number;
  completedQty: number;
  rejectedQty: number;
  completionPct: number;
  status: string;
  startDate: string;
  endDate: string;
  warehouseName: string;
}

interface MaterialConsumptionRow {
  id: string;
  itemName: string;
  itemCode: string;
  itemType: string;
  unit: string;
  orderCount: number;
  bomQty: number;
  actualConsumed: number;
  varianceQty: number;
  variancePct: number | null;
  unitCost: number;
  varianceValue: number;
}

interface ReportSummary {
  productionEntries: number;
  goodQty: number;
  rejectedQty: number;
  workMinutes: number;
  downtimeEntries: number;
  downtimeMinutes: number;
  inspections: number;
  inspectionsPassed: number;
  inspectionsFailed: number;
  inspectionsPending: number;
  openNcrs: number;
  costedOrders: number;
  standardCost: number;
  actualCost: number;
}

type ViewTab = 'all' | 'orders' | 'materials' | 'quick_links';

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Status' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PLANNED', label: 'Planned' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  PLANNED: 'bg-blue-50 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
  ON_HOLD: 'bg-purple-50 text-purple-700 border-purple-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-50 text-red-700 border-red-200',
};

function displayDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export default function ManufacturingReportsPage() {
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const activeWarehouseId = selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined;

  const [activeTab, setActiveTab] = useState<ViewTab>('all');
  // Default period: the current month to date
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return toInputDate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [toDate, setToDate] = useState(() => toInputDate(new Date()));
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const [productionOrders, setProductionOrders] = useState<ProductionSummaryRow[]>([]);
  const [materials, setMaterials] = useState<MaterialConsumptionRow[]>([]);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData('api/v1/reports/manufacturing', {
        fromDate,
        toDate,
        status: statusFilter,
        type: typeFilter,
        ...(activeWarehouseId ? { warehouseId: activeWarehouseId } : {}),
      });

      if (!res?.success || !res?.data) {
        setProductionOrders([]);
        setMaterials([]);
        setSummary(null);
        setError(res?.message || 'Could not load the manufacturing report');
        return;
      }

      setProductionOrders(
        (res.data.productionSummary || []).map((po: any) => {
          const planned = Number(po.planned_qty) || 0;
          const completed = Number(po.completed_qty) || 0;
          return {
            id: po.id,
            poNumber: po.po_number || '',
            product: po.product || '',
            productCode: po.product_code || '',
            type: po.type || '',
            plannedQty: planned,
            completedQty: completed,
            rejectedQty: Number(po.rejected_qty) || 0,
            completionPct: planned > 0 ? Math.round((completed / planned) * 1000) / 10 : 0,
            status: po.status || '',
            startDate: displayDate(po.actual_start_date || po.planned_start_date),
            endDate: displayDate(po.actual_end_date || po.planned_end_date),
            warehouseName: po.warehouse_name || '',
          };
        }),
      );

      setMaterials(
        (res.data.materials || []).map((m: any) => {
          const bomQty = Number(m.bom_qty) || 0;
          const actual = Number(m.actual_consumed) || 0;
          const unitCost = Number(m.unit_cost) || 0;
          const variance = actual - bomQty;
          return {
            id: m.id,
            itemName: m.item_name || '',
            itemCode: m.item_code || '',
            itemType: String(m.item_type || '').replace(/_/g, ' '),
            unit: m.unit || '',
            orderCount: Number(m.order_count) || 0,
            bomQty,
            actualConsumed: actual,
            varianceQty: variance,
            variancePct: bomQty > 0 ? (variance / bomQty) * 100 : null,
            unitCost,
            varianceValue: variance * unitCost,
          };
        }),
      );

      setSummary(res.data.summary || null);
    } catch (e: any) {
      setProductionOrders([]);
      setMaterials([]);
      setSummary(null);
      setError(e?.message || 'Could not load the manufacturing report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, statusFilter, typeFilter, activeWarehouseId]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Production totals
  const totals = useMemo(() => {
    const planned = productionOrders.reduce((s, po) => s + po.plannedQty, 0);
    const completed = productionOrders.reduce((s, po) => s + po.completedQty, 0);
    return {
      planned,
      completed,
      rejected: productionOrders.reduce((s, po) => s + po.rejectedQty, 0),
      completionPct: planned > 0 ? (completed / planned) * 100 : 0,
      completedOrders: productionOrders.filter((po) => po.status === 'COMPLETED').length,
    };
  }, [productionOrders]);

  // Material totals
  const materialTotals = useMemo(
    () => ({
      requiredValue: materials.reduce((s, m) => s + m.bomQty * m.unitCost, 0),
      actualValue: materials.reduce((s, m) => s + m.actualConsumed * m.unitCost, 0),
      varianceValue: materials.reduce((s, m) => s + m.varianceValue, 0),
    }),
    [materials],
  );

  const handleExportOrders = () => {
    const columns = ['PO Number', 'Product', 'Code', 'Type', 'Planned Qty', 'Completed Qty', 'Rejected Qty', 'Completion %', 'Status', 'Start Date', 'End Date'];
    exportToExcel(
      productionOrders.map((o) => ({
        'PO Number': o.poNumber,
        Product: o.product,
        Code: o.productCode,
        Type: o.type,
        'Planned Qty': o.plannedQty,
        'Completed Qty': o.completedQty,
        'Rejected Qty': o.rejectedQty,
        'Completion %': `${o.completionPct}%`,
        Status: o.status.replace(/_/g, ' '),
        'Start Date': o.startDate,
        'End Date': o.endDate,
      })),
      columns.map((c) => ({ header: c, key: c })),
      'manufacturing_report_production_summary',
    );
  };

  const handleExportMaterials = () => {
    const columns = ['Item Name', 'Item Code', 'Item Type', 'Unit', 'Orders', 'BOM Required', 'Actual Issued', 'Variance', 'Variance %', 'Unit Cost', 'Variance Value'];
    exportToExcel(
      materials.map((m) => ({
        'Item Name': m.itemName,
        'Item Code': m.itemCode,
        'Item Type': m.itemType,
        Unit: m.unit,
        Orders: m.orderCount,
        'BOM Required': Number(m.bomQty.toFixed(3)),
        'Actual Issued': Number(m.actualConsumed.toFixed(3)),
        Variance: Number(m.varianceQty.toFixed(3)),
        'Variance %': m.variancePct === null ? '' : `${m.variancePct.toFixed(1)}%`,
        'Unit Cost': m.unitCost,
        'Variance Value': Math.round(m.varianceValue),
      })),
      columns.map((c) => ({ header: c, key: c })),
      'manufacturing_report_material_consumption',
    );
  };

  const s = summary;
  const totalUnits = (s?.goodQty || 0) + (s?.rejectedQty || 0);
  const decided = (s?.inspectionsPassed || 0) + (s?.inspectionsFailed || 0);
  const costVariance = (s?.actualCost || 0) - (s?.standardCost || 0);
  const quickLinks = [
    {
      title: 'Shop Floor & OEE',
      icon: 'ri-cpu-line',
      tone: 'bg-blue-50 text-blue-600',
      path: '/manufacturing/live',
      lines: s
        ? [
            `${s.productionEntries} production entries · ${formatQty(s.goodQty)} good units`,
            `${(s.workMinutes / 60).toFixed(1)} h worked · ${s.downtimeMinutes} min downtime (${s.downtimeEntries})`,
          ]
        : [],
    },
    {
      title: 'Rejections',
      icon: 'ri-close-circle-line',
      tone: 'bg-rose-50 text-rose-600',
      path: '/manufacturing/shop-floor',
      lines: s
        ? [
            `${formatQty(s.rejectedQty)} unit(s) rejected`,
            totalUnits > 0 ? `${((s.rejectedQty / totalUnits) * 100).toFixed(1)}% of ${formatQty(totalUnits)} units made` : 'No output recorded',
          ]
        : [],
    },
    {
      title: 'Quality Inspections',
      icon: 'ri-shield-check-line',
      tone: 'bg-emerald-50 text-emerald-600',
      path: '/manufacturing/qc/inprocess',
      lines: s
        ? [
            `${s.inspections} inspection(s) · ${s.inspectionsPending} pending`,
            decided > 0
              ? `${((s.inspectionsPassed / decided) * 100).toFixed(1)}% pass rate · ${s.openNcrs} open NCR(s)`
              : `No completed inspections · ${s.openNcrs} open NCR(s)`,
          ]
        : [],
    },
    {
      title: 'Production Costing',
      icon: 'ri-money-rupee-circle-line',
      tone: 'bg-amber-50 text-amber-600',
      path: '/manufacturing/costing',
      lines: s
        ? s.costedOrders > 0
          ? [
              `${s.costedOrders} order(s) costed · actual ${formatINR(s.actualCost)}`,
              `Standard ${formatINR(s.standardCost)} · variance ${costVariance > 0 ? '+' : ''}${formatINR(costVariance)}`,
            ]
          : ['No orders in this period have been costed yet']
        : [],
    },
  ];

  const tabs: { key: ViewTab; label: string }[] = [
    { key: 'all', label: 'All Reports' },
    { key: 'orders', label: `Production Summary (${productionOrders.length})` },
    { key: 'materials', label: `Material Consumption vs BOM (${materials.length})` },
    { key: 'quick_links', label: 'Shop Floor, Quality & Costing' },
  ];

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen">
        {/* Header bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">Manufacturing Reports</h2>
            <p className="text-xs text-[#64748b] mt-0.5">
              Production orders planned between {displayDate(fromDate)} and {displayDate(toDate)}
            </p>
          </div>

          <div className="flex items-center flex-wrap gap-2 text-xs">
            <span className="text-[#64748b] font-medium">From</span>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => e.target.value && setFromDate(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            <span className="text-[#64748b] font-medium">To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => e.target.value && setToDate(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-[#e2e8f0] bg-white text-xs text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
            />
            <button
              onClick={fetchReportData}
              disabled={loading}
              className="h-8 px-4 rounded-lg bg-[#4f46e5] text-white font-medium hover:bg-indigo-700 transition-colors cursor-pointer flex items-center gap-1.5 disabled:opacity-60"
            >
              <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">{error}</div>
        )}

        {/* View tabs */}
        <div className="flex items-center flex-wrap gap-2 border-b border-[#e2e8f0] pb-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                activeTab === t.key ? 'bg-[#4f46e5] text-white shadow-sm' : 'bg-white text-[#475569] border border-[#e2e8f0] hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Section 1: Production Summary */}
        {(activeTab === 'all' || activeTab === 'orders') && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-[#1e293b]">Production Summary</h3>
              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-7 px-2 text-xs border border-[#e2e8f0] rounded bg-white text-[#475569] focus:outline-none cursor-pointer"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-7 px-2 text-xs border border-[#e2e8f0] rounded bg-white text-[#475569] focus:outline-none cursor-pointer"
                >
                  <option value="ALL">All Types</option>
                  <option value="MTO">MTO</option>
                  <option value="MTS">MTS</option>
                </select>
                <button
                  onClick={handleExportOrders}
                  disabled={productionOrders.length === 0}
                  className="h-7 px-2.5 text-xs border border-[#e2e8f0] rounded bg-white text-[#1e293b] hover:bg-slate-50 font-medium flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-download-2-line text-xs" /> Export
                </button>
              </div>
            </div>

            <div className={`overflow-auto ${activeTab === 'all' ? 'max-h-[340px]' : 'max-h-[calc(100vh-260px)]'}`}>
              <table className="w-full text-xs text-left">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] tracking-wide">
                    <th className="px-4 py-3">PO NUMBER</th>
                    <th className="px-4 py-3">PRODUCT</th>
                    <th className="px-4 py-3">TYPE</th>
                    <th className="px-4 py-3 text-right">PLANNED</th>
                    <th className="px-4 py-3 text-right">COMPLETED</th>
                    <th className="px-4 py-3 text-right">REJECTED</th>
                    <th className="px-4 py-3 text-right">COMPLETION</th>
                    <th className="px-4 py-3 text-center">STATUS</th>
                    <th className="px-4 py-3">START</th>
                    <th className="px-4 py-3">END</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {productionOrders.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-[#94a3b8]">
                        {loading ? 'Loading production orders…' : 'No production orders in this period'}
                      </td>
                    </tr>
                  )}
                  {productionOrders.map((po) => {
                    const pctColor = po.completionPct >= 100 ? 'text-emerald-600' : po.completionPct < 30 ? 'text-red-500' : 'text-amber-600';
                    return (
                      <tr
                        key={po.id}
                        onClick={() => navigate(`/manufacturing/production-orders/${po.id}`)}
                        className="hover:bg-slate-50/70 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-2.5 font-medium text-[#4f46e5] whitespace-nowrap">{po.poNumber}</td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-[#1e293b]">{po.product}</div>
                          {po.productCode && <div className="text-[11px] text-[#94a3b8]">{po.productCode}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-[#64748b]">{po.type}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-[#1e293b]">{formatQty(po.plannedQty)}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-[#1e293b]">{formatQty(po.completedQty)}</td>
                        <td className={`px-4 py-2.5 text-right font-medium ${po.rejectedQty > 0 ? 'text-red-500' : 'text-[#64748b]'}`}>
                          {formatQty(po.rejectedQty)}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${pctColor}`}>{po.completionPct.toFixed(1)}%</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold border ${STATUS_BADGE[po.status] || STATUS_BADGE.DRAFT}`}>
                            {po.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[#64748b] whitespace-nowrap">{po.startDate}</td>
                        <td className="px-4 py-2.5 text-[#64748b] whitespace-nowrap">{po.endDate}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {productionOrders.length > 0 && (
                  <tfoot className="sticky bottom-0">
                    <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-semibold text-xs text-[#1e293b]">
                      <td className="px-4 py-2.5" colSpan={3}>
                        Total ({productionOrders.length} orders)
                      </td>
                      <td className="px-4 py-2.5 text-right">{formatQty(totals.planned)}</td>
                      <td className="px-4 py-2.5 text-right">{formatQty(totals.completed)}</td>
                      <td className="px-4 py-2.5 text-right">{formatQty(totals.rejected)}</td>
                      <td className="px-4 py-2.5 text-right">{totals.completionPct.toFixed(1)}%</td>
                      <td className="px-4 py-2.5 text-center" colSpan={3}>
                        Completed: {totals.completedOrders} / {productionOrders.length}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Section 2: Material Consumption vs BOM */}
        {(activeTab === 'all' || activeTab === 'materials') && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3.5 border-b border-[#e2e8f0] flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[#1e293b]">Material Consumption vs BOM</h3>
                <p className="text-[11px] text-[#94a3b8] mt-0.5">
                  BOM requirement against material issued, for the orders above (drafts and cancelled orders excluded)
                </p>
              </div>
              <button
                onClick={handleExportMaterials}
                disabled={materials.length === 0}
                className="h-7 px-2.5 text-xs border border-[#e2e8f0] rounded bg-white text-[#1e293b] hover:bg-slate-50 font-medium flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-download-2-line text-xs" /> Export
              </button>
            </div>

            <div className={`overflow-auto ${activeTab === 'all' ? 'max-h-[340px]' : 'max-h-[calc(100vh-260px)]'}`}>
              <table className="w-full text-xs text-left">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0] text-[11px] font-semibold text-[#64748b] tracking-wide">
                    <th className="px-4 py-3">ITEM</th>
                    <th className="px-4 py-3">TYPE</th>
                    <th className="px-4 py-3 text-right">ORDERS</th>
                    <th className="px-4 py-3 text-right">BOM REQUIRED</th>
                    <th className="px-4 py-3 text-right">ACTUAL ISSUED</th>
                    <th className="px-4 py-3 text-right">VARIANCE</th>
                    <th className="px-4 py-3 text-right">VARIANCE %</th>
                    <th className="px-4 py-3 text-right">UNIT COST</th>
                    <th className="px-4 py-3 text-right">VARIANCE VALUE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {materials.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-[#94a3b8]">
                        {loading ? 'Loading materials…' : 'No material requirements for the orders in this period'}
                      </td>
                    </tr>
                  )}
                  {materials.map((m) => {
                    const tone = m.varianceQty > 0.0005 ? 'text-red-500' : m.varianceQty < -0.0005 ? 'text-emerald-600' : 'text-[#64748b]';
                    return (
                      <tr key={m.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-[#1e293b]">{m.itemName}</div>
                          {m.itemCode && <div className="text-[11px] text-[#94a3b8]">{m.itemCode}</div>}
                        </td>
                        <td className="px-4 py-2.5">
                          {m.itemType && (
                            <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-700 uppercase tracking-wide">
                              {m.itemType}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-[#64748b]">{m.orderCount}</td>
                        <td className="px-4 py-2.5 text-right text-[#64748b] font-medium whitespace-nowrap">
                          {m.bomQty.toFixed(2)} {m.unit}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-[#1e293b] whitespace-nowrap">
                          {m.actualConsumed.toFixed(2)} {m.unit}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-medium ${tone}`}>
                          {m.varianceQty > 0.0005 ? '↑ ' : m.varianceQty < -0.0005 ? '↓ ' : ''}
                          {Math.abs(m.varianceQty).toFixed(2)}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-medium ${tone}`}>
                          {m.variancePct === null ? '—' : `${m.variancePct.toFixed(1)}%`}
                        </td>
                        <td className="px-4 py-2.5 text-right text-[#64748b]">{formatINR(m.unitCost)}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${tone}`}>
                          {m.varianceValue > 0 ? '+' : ''}
                          {formatINR(Math.round(m.varianceValue))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {materials.length > 0 && (
                  <tfoot className="sticky bottom-0">
                    <tr className="bg-[#f8fafc] border-t-2 border-[#e2e8f0] font-semibold text-xs text-[#1e293b]">
                      <td className="px-4 py-2.5" colSpan={3}>
                        Total ({materials.length} items)
                      </td>
                      <td className="px-4 py-2.5 text-right">{formatINR(Math.round(materialTotals.requiredValue))}</td>
                      <td className="px-4 py-2.5 text-right">{formatINR(Math.round(materialTotals.actualValue))}</td>
                      <td className="px-4 py-2.5" colSpan={3} />
                      <td className={`px-4 py-2.5 text-right ${materialTotals.varianceValue > 0 ? 'text-red-500' : materialTotals.varianceValue < 0 ? 'text-emerald-600' : ''}`}>
                        {materialTotals.varianceValue > 0 ? '+' : ''}
                        {formatINR(Math.round(materialTotals.varianceValue))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Section 3: Shop floor, quality and costing for the same orders */}
        {(activeTab === 'all' || activeTab === 'quick_links') && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
              Shop Floor, Quality &amp; Costing — for the orders in this period
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {quickLinks.map((q) => (
                <div
                  key={q.title}
                  onClick={() => navigate(q.path)}
                  className="bg-white border border-[#e2e8f0] rounded-xl p-4 hover:border-[#4f46e5]/40 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${q.tone}`}>
                      <i className={`${q.icon} text-lg`} />
                    </div>
                    <h4 className="text-sm font-semibold text-[#1e293b] group-hover:text-[#4f46e5] transition-colors">{q.title}</h4>
                  </div>
                  {q.lines.length === 0 ? (
                    <p className="text-xs text-[#94a3b8]">{loading ? 'Loading…' : 'No data'}</p>
                  ) : (
                    q.lines.map((line) => (
                      <p key={line} className="text-xs text-[#64748b] leading-relaxed">{line}</p>
                    ))
                  )}
                  <div className="mt-2.5 flex items-center gap-1 text-xs text-[#4f46e5] font-medium">
                    <span>Open</span>
                    <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
